import React from 'react';

import { pathwayLabel, READINESS_DIMS, statusBg, statusBorder, VISIT_TYPES } from '../../constants';
import { athenaUrl, daysUntil, getPatient, getTask, overallReadiness } from '../../helpers';
import AthenaLink from '../AthenaLink';
import { Badge, StatusDot } from '../Badge';
import TaskRow from '../TaskRow';
import styles from './ReadinessDetail.module.scss';

const ReadinessDetail = ({ appt, onClose, onSelectTask }) => {
  if (!appt) return null;

  const readiness = appt?.readiness || {};
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const visitType = VISIT_TYPES[appt.visitType];
  const overall = overallReadiness(readiness);
  const dimensions = Object.entries(readiness);
  const days = appt?.date ? daysUntil(appt.date) : null;
  const hasValidDays = Number.isFinite(days);
  const gateLabel = hasValidDays
    ? days <= 2
      ? 'T-2 (final check)'
      : days <= 7
        ? 'T-7 (action needed)'
        : 'T-14+ (on track)'
    : '—';
  const athenaLink = athenaUrl(patient?.athenaId);

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTop}>
          <div>
            <div className={styles.patientName}>{patient?.name}</div>
            <div className={styles.visitType}>{visitType?.label}</div>
          </div>
          <button type="button" onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        <div className={styles.badgesRow}>
          <Badge variant={overall}>
            {overall === 'green' ? 'READY' : overall === 'yellow' ? 'AT RISK' : 'NOT READY'}
          </Badge>
          <Badge variant="outline">
            {appt.date} · {appt.time}
          </Badge>
          <Badge variant="outline">{gateLabel}</Badge>
        </div>
        {visitType?.description && (
          <div className={styles.visitDescription}>{visitType.description}</div>
        )}
      </div>

      <div className={styles.sectionInfo}>
        <div className={styles.infoGrid}>
          {[
            ['Clinician', appt.clinician],
            ['Location', appt.location],
            ['Pathway', pathwayLabel[patient?.pathway]],
            ['Phase', patient?.phase],
          ].map(([key, value], index) => (
            <div key={index} className={styles.infoRow}>
              <span className={styles.infoLabel}>{key}</span>
              <span className={styles.infoValue}>{value}</span>
            </div>
          ))}
        </div>
        {patient && <AthenaLink patient={patient} />}
        {appt.noteUrl && (
          <a
            href={appt.noteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.noteLink}
          >
            <div className={styles.noteBadge}>
              <span className={styles.noteBadgeText}>N</span>
            </div>
            <div className={styles.noteMeta}>
              <span className={styles.noteTitle}>View Signed Visit Note</span>
              <span className={styles.noteSub}>99205 · {appt.date}</span>
            </div>
            <span className={styles.noteArrow}>↗</span>
          </a>
        )}
      </div>

      <div className={styles.sectionNeeds}>
        <div className={styles.sectionLabel}>What needs to be true for this visit</div>
        <div className={styles.dimensions}>
          {dimensions.map(([key, value]) => {
            const dimension = READINESS_DIMS[key];
            const linkedTasks = (value.tasks || []).map(getTask).filter(Boolean);

            return (
              <div
                key={key}
                className={styles.dimensionCard}
                style={{
                  '--dim-bg': statusBg[value.status],
                  '--dim-border': statusBorder[value.status],
                }}
              >
                <div className={styles.dimensionHeader}>
                  <StatusDot status={value.status} />
                  <span className={styles.dimensionTitle}>{dimension?.label || key}</span>
                  <span className={styles.dimensionSpacer} />
                  <Badge variant={value.status}>
                    {value.status === 'green'
                      ? 'Ready'
                      : value.status === 'yellow'
                        ? 'At risk'
                        : 'Not ready'}
                  </Badge>
                </div>
                <div
                  className={styles.dimensionDetail}
                  style={{ marginBottom: linkedTasks.length > 0 ? 8 : 0 }}
                >
                  {value.detail}
                </div>
                {linkedTasks.length > 0 && (
                  <div className={styles.tasksBox}>
                    <div className={styles.tasksHeader}>
                      <span className={styles.tasksTitle}>To do</span>
                    </div>
                    {linkedTasks.map((task) => (
                      <TaskRow key={task.id} task={task} compact onSelect={onSelectTask} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.sectionActions}>
        <div className={styles.actionsRow}>
          <a
            href={athenaLink || undefined}
            target={athenaLink ? '_blank' : undefined}
            rel={athenaLink ? 'noopener noreferrer' : undefined}
            className={`${styles.rescheduleLink} ${athenaLink ? '' : styles.disabledLink}`}
            aria-disabled={!athenaLink}
            onClick={(e) => {
              if (!athenaLink) e.preventDefault();
            }}
          >
            Reschedule in Athena ↗
          </a>
          <button type="button" className={styles.primaryAction}>
            {overall === 'red' ? 'Escalate' : 'Confirm Ready'}
          </button>
        </div>
        {overall === 'red' && hasValidDays && days <= 2 && (
          <div className={styles.finalAlert}>
            <span className={styles.finalAlertTitle}>Final check alert:</span> This visit has
            unresolved blockers within 48 hours. Consider rescheduling or escalating to the
            clinician.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadinessDetail;
